import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { LoggerService } from '@/shared/logger';
import { deepLinkToPath } from './lib/invite';

export interface NavigableRouter {
  navigate: (to: string) => unknown;
  state: { location: { pathname: string } };
}

/**
 * Routes invite URLs delivered by the OS into whichever router is current.
 *
 * Lifecycle covered (iOS and Android, via @capacitor/app):
 * - closed:      `App.getLaunchUrl()` on first mount (cold start)
 * - background / foreground: the `appUrlOpen` event
 *
 * A link that arrives while no router is attached (e.g. between the public
 * and account router swap after sign-in) is queued and delivered as soon as
 * the next router attaches, so it is never dropped. A link for the page that
 * is already showing is ignored, which also de-duplicates the launch URL
 * that iOS may report through both channels on a cold start.
 */
export const createDeepLinkController = () => {
  let router: NavigableRouter | null = null;
  let pending: string | null = null;

  const go = (path: string) => {
    if (!router) {
      pending = path;
      return;
    }
    if (router.state.location.pathname === path) return;
    LoggerService.info('Opening invite deep link');
    router.navigate(path);
  };

  return {
    /** Returns true when the url was a recognised invite. */
    handleUrl(url?: string | null): boolean {
      const path = deepLinkToPath(url);
      if (!path) return false;
      go(path);
      return true;
    },
    attach(next: NavigableRouter) {
      router = next;
      if (pending) {
        const path = pending;
        pending = null;
        go(path);
      }
      return () => {
        if (router === next) router = null;
      };
    },
    get pendingPath() {
      return pending;
    },
  };
};

const controller = createDeepLinkController();
let nativeListening = false;

const startNativeListening = () => {
  if (nativeListening) return;
  nativeListening = true;

  // One app-lifetime listener: re-registering per router would leave a gap
  // where a background → foreground link could be missed.
  App.addListener('appUrlOpen', event => {
    controller.handleUrl(event.url);
  }).catch(() => undefined);

  App.getLaunchUrl()
    .then(res => controller.handleUrl(res?.url))
    .catch(() => undefined);
};

/**
 * Native only: open `/join/<roomID>` when the app is launched or resumed from
 * an invite link. Universal links additionally need the Associated Domains
 * entitlement plus a deployed apple-app-site-association file; see
 * docs/ios-deep-links.md.
 */
export const useDeepLinks = (router: NavigableRouter) => {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const detach = controller.attach(router);
    startNativeListening();
    return detach;
  }, [router]);
};
